import type Phaser from "phaser";
import type { LeaderMirrorOverlay } from "@cold-case/shared/player-follow";

import {
  appendRadioCommsMessage,
  clearDispatchUnread,
  getDispatchUnreadCount,
  initRadioCommsLog,
  initRadioDispatch,
} from "./radio-dispatch.js";

const RADIO_TAB_LABELS = ["Radio Comms", "Dispatch Log"] as const;

let wired = false;
let overlayEl: HTMLElement | null = null;
let commsBodyEl: HTMLDivElement | null = null;
let dispatchBodyEl: HTMLDivElement | null = null;
let inputEl: HTMLInputElement | null = null;
let activeTab = 0;

export type RadioOverlayWire = {
  setPlayerOverlayOpen: (key: "radio", open: boolean) => void;
  isAccusationPhaseActive: () => boolean;
  accusationBlockedMessage: () => string;
  blockIfFollowingLeader: () => boolean;
  isFollowingLeader: () => boolean;
  leaderHasFollowers: () => boolean;
  emitLeaderMirror: (payload: LeaderMirrorOverlay) => void;
  getManualOpen: () => boolean;
  dismissNpcConversationUi: () => void;
  focusPlayfieldCanvas: () => void;
  ensureGlobalCaptureEnabled: () => void;
  showGameStatusMsg: (msg: string, ms?: number) => void;
  getSessionId: () => string | null;
  caseResolved: () => boolean;
  investigationLocked: () => boolean;
  emitRadio: (text: string) => void;
  syncVisualViewportBounds: () => void;
};

let hooks: RadioOverlayWire | null = null;

function el<T extends HTMLElement = HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
}

function buildMirrorPayload(): LeaderMirrorOverlay {
  return {
    kind: "overlay",
    manualOpen: hooks?.getManualOpen() ?? false,
    radioOpen: isRadioOverlayOpen(),
    radioTab: activeTab,
  };
}

export function isRadioOverlayOpen(): boolean {
  return overlayEl?.classList.contains("open") === true;
}

export function getRadioTab(): number {
  return activeTab;
}

export function setRadioTabFromMirror(tab: number): void {
  selectRadioTab(tab);
}

const DISPATCH_LOG_TAB = 1;

function selectRadioTab(tab: number): void {
  activeTab = Math.max(0, Math.min(RADIO_TAB_LABELS.length - 1, tab));
  if (activeTab === DISPATCH_LOG_TAB) clearDispatchUnread();
  renderRadioTabs();
}

function renderRadioTabs(): void {
  const tabsEl = document.getElementById("radio-tabs");
  if (!tabsEl || !commsBodyEl || !dispatchBodyEl) return;
  const dispatchUnread = getDispatchUnreadCount() > 0;
  tabsEl.innerHTML = "";
  RADIO_TAB_LABELS.forEach((label, i) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tab";
    if (i === activeTab) btn.classList.add("active");
    const labelSpan = document.createElement("span");
    labelSpan.className = "radio-tab-label";
    labelSpan.textContent = label;
    btn.append(labelSpan);
    if (i === DISPATCH_LOG_TAB && dispatchUnread) {
      const dot = document.createElement("span");
      dot.className = "radio-tab-unread-dot";
      dot.setAttribute("aria-hidden", "true");
      btn.append(dot);
    }
    btn.onclick = () => {
      if (hooks?.isFollowingLeader()) return;
      selectRadioTab(i);
      if (!hooks?.isFollowingLeader() && hooks?.leaderHasFollowers()) {
        hooks.emitLeaderMirror(buildMirrorPayload());
      }
    };
    tabsEl.append(btn);
  });
  const showComms = activeTab === 0;
  commsBodyEl.style.display = showComms ? "block" : "none";
  dispatchBodyEl.style.display = showComms ? "none" : "block";
  if (showComms) {
    inputEl?.removeAttribute("disabled");
  } else {
    inputEl?.setAttribute("disabled", "true");
  }
}

export function setRadioOpen(open: boolean, opts?: { fromMirror?: boolean }): void {
  if (!overlayEl || !hooks) return;
  if (open && hooks.isAccusationPhaseActive()) return;
  hooks.setPlayerOverlayOpen("radio", open);
  overlayEl.classList.toggle("open", open);
  overlayEl.classList.toggle(
    "radio-follower-readonly",
    hooks.isFollowingLeader() && open
  );
  if (!opts?.fromMirror && !hooks.isFollowingLeader() && hooks.leaderHasFollowers()) {
    hooks.emitLeaderMirror(buildMirrorPayload());
  }
  if (open) {
    overlayEl.setAttribute("aria-hidden", "false");
    hooks.syncVisualViewportBounds();
    if (activeTab === DISPATCH_LOG_TAB) clearDispatchUnread();
    renderRadioTabs();
    requestAnimationFrame(() => {
      if (activeTab === 0) inputEl?.focus();
    });
  } else {
    overlayEl.setAttribute("aria-hidden", "true");
    inputEl?.blur();
    hooks.focusPlayfieldCanvas();
    hooks.ensureGlobalCaptureEnabled();
  }
}

export function wireRadioOverlay(game: Phaser.Game, wire: RadioOverlayWire): void {
  hooks = wire;
  overlayEl = el("radio-overlay");
  commsBodyEl = el<HTMLDivElement>("radio-comms-body");
  dispatchBodyEl = el<HTMLDivElement>("radio-dispatch-body");
  inputEl = el<HTMLInputElement>("radio-input");
  initRadioDispatch(dispatchBodyEl, game);
  initRadioCommsLog(commsBodyEl);

  if (!wired) {
    wired = true;
    game.events.on("ccc_radio_dispatch_unread", renderRadioTabs);
    renderRadioTabs();
    el<HTMLButtonElement>("radio-close").onclick = () => setRadioOpen(false);
    el<HTMLButtonElement>("radio-send").onclick = () => sendRadioMessage();
    inputEl.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") {
        e.preventDefault();
        sendRadioMessage();
      }
    });
    inputEl.addEventListener("keyup", (e) => e.stopPropagation());
    inputEl.addEventListener("keypress", (e) => e.stopPropagation());
    overlayEl.addEventListener("click", (ev) => {
      if (ev.target === overlayEl) setRadioOpen(false);
    });
  }
}

function sendRadioMessage(): void {
  if (!hooks || !inputEl) return;
  if (hooks.blockIfFollowingLeader()) return;
  if (!hooks.getSessionId() || hooks.caseResolved() || hooks.investigationLocked()) return;
  const text = inputEl.value.trim();
  if (!text) return;
  hooks.emitRadio(text);
  inputEl.value = "";
}

export function onGameRadioMessage(m: {
  displayName?: string;
  username?: string;
  text: string;
}): void {
  const label = m.displayName?.trim() || m.username?.trim() || "Detective";
  appendRadioCommsMessage(label, m.text);
}

export function toggleRadioFromHud(): void {
  if (!hooks) return;
  if (hooks.blockIfFollowingLeader()) return;
  const opening = !isRadioOverlayOpen();
  if (opening && hooks.isAccusationPhaseActive()) {
    hooks.showGameStatusMsg(hooks.accusationBlockedMessage());
    return;
  }
  if (opening) hooks.dismissNpcConversationUi();
  setRadioOpen(opening);
}
